import type { ReactNode } from 'react'
import { AlertTriangle, Inbox, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { ErrorApi } from '@/api/cliente'

/**
 * Los tres estados que toda lista del panel tiene que saber mostrar: cargando,
 * vacia y rota. Un error de red degrada la pantalla y ofrece reintentar; no la
 * deja en blanco.
 */

export function EstadoCargando({ etiqueta = 'Cargando…', filas = 3 }: { etiqueta?: string; filas?: number }) {
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        {etiqueta}
      </p>
      {Array.from({ length: filas }, (_, indice) => (
        <Skeleton key={indice} className="h-12 w-full" />
      ))}
    </div>
  )
}

export function EstadoVacio({
  titulo,
  detalle,
  accion,
}: {
  titulo: string
  detalle?: string
  accion?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-8 text-center">
      <Inbox className="size-6 text-muted-foreground" aria-hidden="true" />
      <p className="font-medium">{titulo}</p>
      {detalle !== undefined && <p className="text-sm text-muted-foreground">{detalle}</p>}
      {accion}
    </div>
  )
}

export function EstadoError({
  error,
  alReintentar,
  reintentando = false,
}: {
  error: ErrorApi
  alReintentar?: () => void
  reintentando?: boolean
}) {
  return (
    <div role="alert" className="flex flex-col items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
      <p className="flex items-center gap-2 text-sm font-medium text-destructive">
        <AlertTriangle className="size-4" aria-hidden="true" />
        {error.esDeRed ? 'Sin conexión con el servidor' : 'No pudimos cargar esto'}
      </p>
      <p className="text-sm text-muted-foreground">{error.mensaje}</p>
      {alReintentar !== undefined && (
        <Button variant="outline" size="sm" onClick={alReintentar} disabled={reintentando}>
          {reintentando ? 'Reintentando…' : 'Reintentar'}
        </Button>
      )}
    </div>
  )
}
